import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TSubmitFormAnswer } from '@onesource/schemas';
import type { TFormQuestion } from '@onesource/services';
import DOMPurify from 'dompurify';

import { QuestionRenderer } from './question-renderer';

const SANITIZE_RICH_HTML_CONFIG = {
  ADD_TAGS: ['iframe', 'video', 'audio', 'source', 'embed'],
  ADD_ATTR: [
    'allow',
    'allowfullscreen',
    'frameborder',
    'scrolling',
    'target',
    'rel',
    'controls',
    'autoplay',
    'loop',
    'muted',
    'playsinline',
    'poster',
  ],
};

interface QuestionBlockProps {
  question: TFormQuestion;
  answer: TSubmitFormAnswer;
  onAnswerChange: (answer: TSubmitFormAnswer) => void;
  disabled?: boolean;
}

/**
 * Renders a single question — required indicator, question text/body/explanation,
 * and the type-specific answer UI. Used for both standalone-question pages and
 * each question inside a group page.
 */
export const QuestionBlock = ({ question, answer, onAnswerChange, disabled = false }: QuestionBlockProps) => {
  return (
    <Box>
      {question.isRequired && (
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: 0.6,
            color: '#ff4d6d',
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          Required *
        </Typography>
      )}
      <Typography
        component="div"
        sx={{
          textAlign: 'center',
          fontSize: '1rem',
          lineHeight: 1.6,
          mb: question.questionBody ? 1.5 : { xs: 2, md: 4 },
          mx: 'auto',
          '& p': { m: 0, mb: 0.5 },
          '& ul, & ol': { pl: 3, m: 0, textAlign: 'left' },
          '& a': { color: '#ff4d6d' },
          '& iframe, & img, & video': { maxWidth: '100%' },
        }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(question.questionText, SANITIZE_RICH_HTML_CONFIG),
        }}
      />

      {question.questionBody && (
        <Typography
          component="div"
          sx={{
            fontSize: '0.95rem',
            lineHeight: 1.6,
            color: 'text.secondary',
            mb: { xs: 2, md: 4 },
            maxWidth: 700,
            mx: 'auto',
            '& p': { m: 0, mb: 1 },
            '& ul, & ol': { pl: 3, m: 0, mb: 1 },
            '& a': { color: '#ff4d6d' },
            '& iframe, & img, & video': { maxWidth: '100%' },
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(question.questionBody, SANITIZE_RICH_HTML_CONFIG),
          }}
        />
      )}

      {question.explanation && (
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: '0.9rem',
            color: 'text.secondary',
            mb: { xs: 2, md: 3 },
            maxWidth: 700,
            mx: 'auto',
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(question.explanation || '', SANITIZE_RICH_HTML_CONFIG),
          }}
        />
      )}

      <Box sx={{ mb: 5 }}>
        <QuestionRenderer question={question} answer={answer} onAnswerChange={onAnswerChange} disabled={disabled} />
      </Box>
    </Box>
  );
};
